import { TemplateField, TemplateSection, Template } from '../_types';

export type FieldInheritanceType = 'same-as-parent' | 'modified-from-parent' | 'new-in-child';

/**
 * Determines the inheritance type of a field by comparing effective (resolved) values
 * According to the inheritance model:
 * - Effective values represent what the user actually sees (after parent locks/unlocks are applied)
 * - Comparing effective values ensures badges reflect the visible state, not underlying raw data
 *
 * @param field - The child template field to check
 * @param parentTemplate - The parent template (from template.parentTemplate)
 * @returns The inheritance type, or null if this is not a child template field
 */
export const getFieldInheritanceType = (
  field: TemplateField,
  parentTemplate?: Template | null
): FieldInheritanceType | null => {
  // If there's no parentSectionFieldId, this field is new in the child template
  if (field.parentSectionFieldId === null) {
    return 'new-in-child';
  }

  // If no parent template is provided, we can't determine inheritance
  if (!parentTemplate) {
    return null;
  }

  // Find the parent field in the parent template using parentSectionFieldId
  let parentField: TemplateField | null = null;
  for (const parentSection of parentTemplate.templateSections) {
    parentField = parentSection.sectionFields.find((f) => f.id === field.parentSectionFieldId) || null;
    if (parentField) break;
  }

  if (!parentField) {
    return null;
  }

  // Check if the field has been deleted (soft delete)
  if (field.isDeleted) {
    return 'modified-from-parent'; // Deleted fields are modifications
  }

  // Compare effective (resolved) values against parent's effective values
  // This ensures badges reflect what the user sees, accounting for parent locks/unlocks
  const hasCustomLabel = field.effectiveLabel !== parentField.effectiveLabel;
  const hasCustomDefaultValue =
    JSON.stringify(field.effectiveDefaultValues ?? []) !== JSON.stringify(parentField.effectiveDefaultValues ?? []);
  const hasCustomRequired = field.effectiveIsRequired !== parentField.effectiveIsRequired;
  const hasCustomLocked = field.effectiveIsLocked !== parentField.effectiveIsLocked;

  // For SingleSelect/MultiSelect fields, also check if options have been customized
  let hasCustomOptions = false;
  if ((field.fieldType === 'SingleSelect' || field.fieldType === 'MultiSelect') && field.sectionFieldOptions && parentField.sectionFieldOptions) {
    // Capture parentField in a const for TypeScript null safety inside callback
    const parentFieldOptions = parentField.sectionFieldOptions;
    hasCustomOptions = field.sectionFieldOptions.some((opt) => {
      const parentOpt = parentFieldOptions.find((po) => po.fieldOptionId === opt.fieldOptionId);
      // Compare effective labels to reflect what the user sees
      return opt.effectiveLabel !== parentOpt?.effectiveLabel;
    });
  }

  // If any effective property differs from parent, it's modified
  if (hasCustomLabel || hasCustomDefaultValue || hasCustomRequired || hasCustomLocked || hasCustomOptions) {
    return 'modified-from-parent';
  }

  // If all effective properties match parent's effective properties, the field is same as parent
  return 'same-as-parent';
};

/**
 * Determines if a field is locked by its parent template
 *
 * A field is considered locked by parent when:
 * - The field's effectiveIsLocked is true AND isLocked is null (direct inheritance from parent)
 *
 * Note: Section lock prevents adding/removing fields but does NOT lock field editing.
 * Only fields explicitly locked in the parent template are locked for editing.
 *
 * @param field - The template field to check
 * @param section - The section containing the field
 * @param parentTemplate - The parent template (optional)
 * @returns true if the field is locked by parent, false otherwise
 */
export const isFieldLockedByParent = (
  field: TemplateField,
  section: TemplateSection,
  parentTemplate?: Template | null
): boolean => {
  // Check if field is directly locked by parent
  // Only return true if the FIELD itself is locked, not the section
  const isDirectlyLocked = !!(
    field.effectiveIsLocked &&
    field.isLocked === null &&
    parentTemplate &&
    field.parentSectionFieldId !== null
  );

  return isDirectlyLocked;
};
